import { CSSProperties, FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'

import { Spinner, LabelIcon, HelpLink } from '../../../components/generic'
import { colors } from '@lmc/common/constants'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import Actions from '../../../actions'
import { Button } from '@lmc/ui-kit'
import { Home } from '@lmc/ts-common'
import { CardTitle, EmptyPills } from './CardComponents'
import { Link } from 'react-router-dom'
import { gaEvent } from 'common/services/analytics'

const SUPPORT_LINK =
  'https://support.logmycare.co.uk/en/collections/2749236-emar'

const REQUEST_ID = 'emar-rounds-card'

// TODO find out what is this type
export type Round = any

export const RoundRow = ({
  time,
  due,
  icon,
  colour,
  statusText,
  notAdministered,
  PRN,
  index,
}: Round) => (
  <>
    <tr style={styles.rowStyle}>
      <td style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
        <div style={{ display: 'flex', flex: 2, fontSize: 14 }}>{time}</div>
        <div style={{ display: 'flex', flex: 3, fontSize: 14 }}>
          {/* @ts-ignore */}
          <LabelIcon
            boldText={due}
            iconName={icon}
            iconFill={colour}
            iconSize={20}
            text={statusText}
            textFill={due > 0 ? colors.black : colors.darkgrey}
          />
        </div>
      </td>
      <td
        style={{
          display: 'flex',
          flex: 1,
          marginLeft: 16,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flex: 3,
            fontSize: 14,
            color: notAdministered ? colors.black : colors.darkgrey,
          }}
        >
          <div style={styles.boldText}>{notAdministered}</div>
          Not administered
        </div>
        <div
          style={{
            display: 'flex',
            flex: 1,
            fontSize: 14,
            color: !PRN ? colors.darkgrey : colors.black,
          }}
        >
          <div style={styles.boldText}>{PRN}</div>
          PRN
        </div>
      </td>
    </tr>
    {index !== 2 && (
      <tr>
        <td>
          <div
            style={{
              height: 1,
              width: '100%',
              backgroundColor: '#DDDDDD',
              marginTop: 0,
              marginBottom: 0,
            }}
          />
        </td>
      </tr>
    )}
  </>
)

const MedicationCard: FC = () => {
  useEffect(() => {
    fetchData()
  }, [])

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const isLoading = useSelector((state) =>
    _.get(state, `data.requests.${REQUEST_ID}.isLoading`)
  )

  const rounds: Round[] =
    useSelector((state) =>
      // @ts-ignore
      Selectors.getMedicationRoundsSummary(state, {
        requestID: REQUEST_ID,
      })
    ) || []

  const dispatch = useDispatch()

  const fetchData = () => {
    dispatch(
      Operations.fetchData({
        url: 'aggregations/emar-dashboard',
        id: REQUEST_ID,
      })
    )
  }

  const openOnboardingModal = () => {
    dispatch(Operations.loadList('emar-round-timings'))
    dispatch(Operations.openModal({ modalId: 'onboardingEmar' }))
    dispatch(
      Actions.addForm({
        formId: 'onboardingEmar',
      })
    )
  }

  const getStatus = (round: Round) => {
    const {
      isDue,
      allSigned,
      scheduled: { due },
    } = round

    const icons = {
      'Not signed': { icon: 'overdue', colour: colors.red },
      Signed: { icon: 'tick-in-circle', colour: colors.successgreen },
      Due: { icon: 'clock', colour: due > 0 ? colors.yellow : colors.darkgrey },
    }

    const statusText = allSigned ? 'Signed' : isDue ? 'Due' : 'Not signed'

    const statusIcon = icons[statusText]

    return { statusText, statusIcon }
  }

  const renderRound = (round: Round, index: number) => {
    const {
      time,
      PRN,
      allSigned,
      scheduled: { due, signed, notAdministered },
    } = round

    const {
      statusText,
      statusIcon: { icon, colour },
    } = getStatus(round)

    return (
      <RoundRow
        key={time}
        time={time}
        index={index}
        due={allSigned ? signed : due}
        icon={icon}
        colour={colour}
        statusText={statusText}
        notAdministered={notAdministered}
        PRN={PRN}
      />
    )
  }

  const renderRounds = (rounds: Round[]) => (
    <div style={{ display: 'flex', flex: 1 }}>
      <table style={{ width: '100%', height: '100%' }}>
        <thead style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <tr style={{ display: 'flex', flex: 1 }}>
            <th style={{ display: 'flex', flex: 2 }}>Round</th>
            <th style={{ display: 'flex', flex: 3 }}>Status</th>
            <th style={{ display: 'flex', flex: 5, marginLeft: 16 }}>
              Details
            </th>
          </tr>
        </thead>
        <tbody>{rounds.map(renderRound)}</tbody>
      </table>
    </div>
  )

  const renderNoRounds = () => (
    <div style={styles.noRoundsContainer as CSSProperties}>
      <EmptyPills />
    </div>
  )

  const triggerGAEvent = ({
    action,
    label,
  }: {
    action: string
    label?: string
  }) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label,
    })

  const renderBody = () => {
    if (isLoading) return <Spinner style={{ paddingTop: 50 }} />
    if (rounds && rounds.length) return renderRounds(rounds)
    return renderNoRounds()
  }

  const renderFooter = () => {
    const link = '/reports/emar/medication-rounds'
    const rounds = null
    const noRounds = !rounds
    const shouldOnboard = noRounds && !home?.features?.emarOnboarded?.enabled
    if (isLoading) return null
    return shouldOnboard ? (
      <div style={styles.footerRightAligned}>
        <Button variant="primary" onClick={() => openOnboardingModal()}>
          Set Up eMAR
        </Button>
      </div>
    ) : (
      <div style={styles.footer}>
        <div style={{ alignSelf: 'center' }}>
          {/* @ts-ignore */}
          <HelpLink supportLink={SUPPORT_LINK} style={styles.supportLink} />
        </div>
        <Link
          onClick={() =>
            triggerGAEvent({
              action: 'Click view all medication rounds',
            })
          }
          to={link}
        >
          <Button variant="default">{VIEW_BUTTON_TEXT}</Button>
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="lmc-home-card" style={styles.container as CSSProperties}>
        <CardTitle title={TITLE} />
        <div className="lmc-home-card-body-ss">{renderBody()}</div>
        <div className="lmc-home-card-footer">{renderFooter()}</div>
      </div>
    </div>
  )
}

const TITLE = 'Medication'

const VIEW_BUTTON_TEXT = 'View All'

const styles = {
  boldText: {
    fontWeight: 700,
    marginRight: 4,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  footerRightAligned: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  noRoundsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
    borderBottom: '1px solid #F0F0F0',
    paddingBottom: 15,
  },
  openResidentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowStyle: {
    display: 'flex',
    flex: 1,
    marginTop: 12,
    marginBottom: 12,
    alignItems: 'center',
  },
  supportLink: {
    display: 'inline-block',
    float: 'left',
    marginTop: 0,
  },
}

export default MedicationCard
